import React, { useMemo, useState } from 'react';
import { createReservation } from '../menu/model';
import { sendReservationEmails } from './notify';
import { downloadTicket } from './ticket';
import { Tile } from './illustrations';
import './reservation.css';

const LOCALE = { tr: 'tr-TR', en: 'en-GB', fr: 'fr-FR', es: 'es-ES' };

const T = {
  tr: {
    email: 'E-posta', brunch: 'Brunch', dinner: 'Akşam yemeği', invalid: 'Ad, telefon ve geçerli bir e-posta gerekli.',
    ticket: 'Bileti indir (PDF)',
    kicker: 'Rezervasyon', title: 'Sofrada yerin olsun.',
    lead: 'Talebini bırak, telefonla dönüp teyit edelim.',
    name: 'Ad soyad', phone: 'Telefon', day: 'Gün', time: 'Saat', guests: 'Kişi', note: 'Not (isteğe bağlı)',
    notePh: 'Alerji, özel gün, masa tercihi...', send: 'Rezervasyon iste', sending: 'Gönderiliyor...',
    doneT: 'Talebin ulaştı.', doneP: 'En kısa sürede seni arayıp teyit edeceğiz.', again: 'Yeni talep',
    err: 'Gönderilemedi. Lütfen tekrar dene.', person: 'kişi',
  },
  en: {
    email: 'Email', brunch: 'Brunch', dinner: 'Dinner', invalid: 'Name, phone and a valid email are required.',
    ticket: 'Download ticket (PDF)',
    kicker: 'Reservation', title: 'Save a seat at the table.',
    lead: 'Leave a request and we will call you back to confirm.',
    name: 'Full name', phone: 'Phone', day: 'Day', time: 'Time', guests: 'Guests', note: 'Note (optional)',
    notePh: 'Allergies, an occasion, a table preference...', send: 'Request a table', sending: 'Sending...',
    doneT: 'Request received.', doneP: 'We will call you shortly to confirm.', again: 'New request',
    err: 'Could not send. Please try again.', person: 'guests',
  },
  fr: {
    email: 'E-mail', brunch: 'Brunch', dinner: 'Dîner', invalid: 'Nom, téléphone et e-mail valide requis.',
    ticket: 'Télécharger le billet (PDF)',
    kicker: 'Réservation', title: 'Une place à table.',
    lead: 'Laissez une demande, nous vous rappelons pour confirmer.',
    name: 'Nom complet', phone: 'Téléphone', day: 'Jour', time: 'Heure', guests: 'Couverts', note: 'Note (facultatif)',
    notePh: 'Allergies, occasion, préférence de table...', send: 'Demander une table', sending: 'Envoi...',
    doneT: 'Demande reçue.', doneP: 'Nous vous rappelons très vite pour confirmer.', again: 'Nouvelle demande',
    err: 'Envoi impossible. Réessayez.', person: 'pers.',
  },
  es: {
    email: 'Correo', brunch: 'Brunch', dinner: 'Cena', invalid: 'Nombre, teléfono y un correo válido son obligatorios.',
    ticket: 'Descargar el ticket (PDF)',
    kicker: 'Reserva', title: 'Un sitio en la mesa.',
    lead: 'Deja tu solicitud y te llamamos para confirmar.',
    name: 'Nombre completo', phone: 'Teléfono', day: 'Día', time: 'Hora', guests: 'Personas', note: 'Nota (opcional)',
    notePh: 'Alergias, una celebración, preferencia de mesa...', send: 'Pedir mesa', sending: 'Enviando...',
    doneT: 'Solicitud recibida.', doneP: 'Te llamamos enseguida para confirmar.', again: 'Nueva solicitud',
    err: 'No se pudo enviar. Inténtalo de nuevo.', person: 'pers.',
  },
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Two sittings: brunch in the morning, dinner in the evening (half-hour slots).
const slots = (from, to) => {
  const out = [];
  for (let m = from * 60; m <= to * 60; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${m % 60 ? '30' : '00'}`);
  return out;
};
const TIMES = { brunch: slots(9, 14), dinner: slots(17, 23.5) };
const DEFAULT_TIME = { brunch: '11:00', dinner: '20:00' };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Reservation = ({ lang = 'tr', id = 'rezervasyon' }) => {
  const t = T[lang] || T.tr;
  const days = useMemo(() => {
    const out = [];
    const base = new Date();
    for (let i = 0; i < 21; i += 1) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      out.push({ value: iso(d), label: d.toLocaleDateString(LOCALE[lang] || 'tr-TR', { weekday: 'short', day: '2-digit', month: '2-digit' }) });
    }
    return out;
  }, [lang]);

  const [form, setForm] = useState({ type: 'dinner', name: '', phone: '', email: '', date: days[0].value, time: DEFAULT_TIME.dinner, guests: 2, note: '' });
  const setType = (type) => setForm((f) => ({ ...f, type, time: DEFAULT_TIME[type] }));
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [msg, setMsg] = useState('');
  const [booked, setBooked] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.replace(/\D/g, '').length < 7 || !EMAIL_RE.test(form.email.trim())) { setMsg(t.invalid); return; }
    setState('sending'); setMsg('');
    try {
      const code = await createReservation({ ...form, guests: Number(form.guests), lang });
      setBooked({ ...form, guests: Number(form.guests), lang, code });
      setState('done');
      // Emails are best effort: the booking is already saved.
      sendReservationEmails({ ...form, guests: Number(form.guests), lang, code }).catch((e) => console.error(e));
    } catch (err) {
      console.error(err);
      setState('error'); setMsg(t.err);
    }
  };

  return (
    <section className="rz" id={id} lang={lang}>
      <div className="rz-card">
        <div className="rz-tile"><Tile theme="oxblood" /></div>
        <p className="rz-kicker">{t.kicker}</p>
        {state === 'done' ? (
          <div className="rz-done" role="status">
            <h2>{t.doneT}</h2>
            <p>{t.doneP}</p>
            {booked && (
              <button type="button" className="rz-btn" onClick={() => downloadTicket(booked).catch((e) => console.error(e))}>{t.ticket}</button>
            )}
            <br />
            <button type="button" className="rz-btn rz-btn--ghost" onClick={() => { setState('idle'); setForm((f) => ({ ...f, note: '' })); }}>{t.again}</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <h2>{t.title}</h2>
            <p className="rz-lead">{t.lead}</p>
            <div className="rz-type" role="group" aria-label={t.time}>
              {['brunch', 'dinner'].map((k) => (
                <button key={k} type="button" aria-pressed={form.type === k} onClick={() => setType(k)}>
                  <b>{t[k]}</b><span>{TIMES[k][0]} – {TIMES[k][TIMES[k].length - 1]}</span>
                </button>
              ))}
            </div>
            <div className="rz-grid">
              <label className="rz-field rz-span2"><span>{t.name}</span>
                <input value={form.name} onChange={set('name')} autoComplete="name" maxLength={80} required />
              </label>
              <label className="rz-field rz-half"><span>{t.phone}</span>
                <input value={form.phone} onChange={set('phone')} type="tel" inputMode="tel" autoComplete="tel" maxLength={24} placeholder="05xx xxx xx xx" required />
              </label>
              <label className="rz-field rz-half"><span>{t.email}</span>
                <input value={form.email} onChange={set('email')} type="email" inputMode="email" autoComplete="email" maxLength={120} required />
              </label>
              <label className="rz-field"><span>{t.day}</span>
                <select value={form.date} onChange={set('date')}>
                  {days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </label>
              <label className="rz-field"><span>{t.time}</span>
                <select value={form.time} onChange={set('time')}>
                  {TIMES[form.type].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              <label className="rz-field"><span>{t.guests}</span>
                <select value={form.guests} onChange={set('guests')}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} {t.person}</option>)}
                </select>
              </label>
              <label className="rz-field rz-span3"><span>{t.note}</span>
                <textarea value={form.note} onChange={set('note')} maxLength={500} rows={3} placeholder={t.notePh} />
              </label>
            </div>
            {msg && <p className="rz-msg" role="alert">{msg}</p>}
            <button type="submit" className="rz-btn" disabled={state === 'sending'}>{state === 'sending' ? t.sending : t.send}</button>
          </form>
        )}
      </div>
    </section>
  );
};

export default Reservation;
